import React from 'react';
import PropTypes from 'prop-types';

/**
 * Individual Template Card Component
 * Displays a single template option with icon, name, and description
 */
function TemplateCard({ 
  template, 
  isSelected, 
  onClick,
  size = 'medium' // 'small', 'medium', 'large'
}) {
  const sizeClasses = {
    small: 'p-3',
    medium: 'p-4',
    large: 'p-5'
  };

  const iconSizeClasses = {
    small: 'text-xl',
    medium: 'text-2xl',
    large: 'text-3xl'
  };

  // Fixed height classes to ensure uniform grid
  const heightClasses = {
    small: 'min-h-[60px]',
    medium: 'min-h-[100px]',
    large: 'min-h-[140px]'
  };

  return (
    <button
      type="button"
      onClick={() => onClick(template)}
      className={`
        w-full h-full text-left rounded-xl border-2 transition-all duration-200
        flex flex-col
        ${sizeClasses[size]}
        ${heightClasses[size]}
        ${isSelected 
          ? 'border-blue-500 bg-blue-500/10 dark:bg-blue-500/20 ring-2 ring-blue-500/30' 
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-750'
        }
      `}
    >
      <div className="flex items-start gap-3 flex-1">
        {/* Icon */}
        <span className={`${iconSizeClasses[size]} flex-shrink-0`} role="img" aria-label={template.name}>
          {template.icon}
        </span>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-medium ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
            {template.name}
          </h4>
          {template.description && size !== 'small' && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
              {template.description}
            </p>
          )}
        </div>

        {/* Selected Indicator */}
        {isSelected && (
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
      
      {/* Example Tags Preview (only for large size) - at the bottom */}
      {size === 'large' && template.exampleTags && template.exampleTags.length > 0 && (
        <div className="mt-auto pt-2 flex flex-wrap gap-1">
          {template.exampleTags.slice(0, 4).map((tag, index) => (
            <span
              key={index}
              className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
            >
              {tag}
            </span>
          ))}
          {template.exampleTags.length > 4 && (
            <span className="inline-block px-2 py-0.5 text-xs text-gray-400">
              +{template.exampleTags.length - 4}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

TemplateCard.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    description: PropTypes.string,
    exampleTags: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large'])
};

TemplateCard.defaultProps = {
  isSelected: false,
  size: 'medium'
};

export default TemplateCard;
